import { useState } from "react";
import NewEvent from "../../../../passapp-sdk/newEvent";
import User from "../../../../passapp-sdk/user";
import Vehicle, { VehicleFileType } from "../../../../passapp-sdk/vehicle";
import { useStore } from "../../../../hooks/useStore";
import useVehicleBrands from "../../../../hooks/useVehicleBrands";
import { EntryRegistrationFormValues } from "../components/types";
import { fileNames } from "../../../../utils/imageUtil";
import { areSameCalendarDate, parseBirthDateValue } from "../utils/visitorPersonalData";
import { parseApiDate, parseApiOrIsoDate } from "../utils/apiDates";
import {
  CommunicationMethod,
  EXIT_CONTROL_KEYS,
  GateControlKey,
} from "../components/AccessTypeForm/TransportContentForm/transportAuthorizationTypes";
import { isTransportCompanionEvent } from "../utils/guestTypeUtils";

/** Los transportistas no pasan por `AccessVehicleForm`, así que `vehicleType` nunca sale de "none".
 * El camión es un vehículo como cualquier otro para el evento, así que se fuerza a "car". */
const isTransportData = (data: EntryRegistrationFormValues) =>
  data.selectedAccessMethod === "transport";

/**
 * Medio de acceso del evento.
 *
 * `exitOnFoot` gana sobre todo: cuando el chofer sale a pie y deja las unidades adentro, el egreso
 * no lleva vehículo aunque el ingreso haya sido de transporte. Sin esto el "car" forzado de arriba
 * hace que `verifyVehicle` recupere la patente de la autorización de egreso y el camión salga
 * marcado como que se fue.
 */
const resolveVehicleType = (data: EntryRegistrationFormValues) => {
  if (data.exitOnFoot) return "none";
  return isTransportData(data) ? "car" : data.vehicleType;
};

/** Id que el egreso le inventa al vehículo del ingreso cuando ese evento no trae uno registrado
 * (ver `buildExitUser` / `prefillUtils`): alcanza para pintar y seleccionar la tarjeta, pero no es
 * un id que el backend pueda resolver. */
const EXIT_VEHICLE_PLACEHOLDER_ID = "exit-vehicle-id";

/**
 * El id del vehículo tal como lo acepta el evento, o undefined cuando lo que hay es un id
 * inventado por el egreso: el placeholder de arriba, o la patente usada como id. En esos casos el
 * vehículo tiene que viajar por patente (`vehicle`), porque mandarlo como `vehicleId` hace que el
 * backend rechace el addEvent.
 */
const usableVehicleId = (vehicle?: { id?: string | null; plate?: string }): string | undefined => {
  const id = vehicle?.id;
  if (!id || id === EXIT_VEHICLE_PLACEHOLDER_ID) return undefined;
  if (vehicle?.plate && id === vehicle.plate) return undefined;
  return id;
};

/**
 * Centralized hook for LogisticPark event operations.
 * Follows the same pattern as useActionEvent but adapted for the
 * LogisticPark RegistroIngreso feature.
 */
const useEvent = () => {
  const { authStore } = useStore();
  const newEvent = new NewEvent();
  const userReq = new User();
  const vehicleApi = new Vehicle();
  const { rawBrands } = useVehicleBrands();
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  /**
   * El select de marcas guarda el nombre. Devuelve además el id del catálogo cuando existe, para
   * poder mandar `brandId` junto al `brand` legacy. Si el catálogo todavía no cargó o la marca no
   * está en él, se manda sólo el texto: es exactamente lo que se mandaba antes.
   */
  const resolveBrand = (value?: string): { brandId?: string; brand: string } => {
    if (!value) return { brand: "" };
    const match = rawBrands.find((brand) => brand.name === value);
    return match?.id ? { brandId: match.id, brand: value } : { brand: value };
  };

  /**
   * Build the event payload from the form values.
   */
  const formatBirthdateForApi = (value: unknown): string => {
    const parsedDate = parseBirthDateValue(value);
    if (!parsedDate) {
      return "";
    }

    const year = parsedDate.getFullYear();
    const month = `${parsedDate.getMonth() + 1}`.padStart(2, "0");
    const day = `${parsedDate.getDate()}`.padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  /**
   * Fechas de vencimiento en el formato DD-MM-YYYY que espera la API
   * (technicalReviewExpirationDate, licencia, ART y — en transportistas —
   * insuranceExpirationDate del alta de vehículo).
   */
  const formatVtvExpirationDateForApi = (value: unknown): string => {
    const parsedDate = value instanceof Date ? value : value ? new Date(value as string) : null;
    if (!parsedDate || isNaN(parsedDate.getTime())) {
      return typeof value === "string" ? value : "";
    }

    const day = `${parsedDate.getDate()}`.padStart(2, "0");
    const month = `${parsedDate.getMonth() + 1}`.padStart(2, "0");
    const year = parsedDate.getFullYear();
    return `${day}-${month}-${year}`;
  };

  /**
   * ISO sólo si la fecha es válida. Un `Date` inválido (una fecha de la API que no se pudo parsear)
   * hacía explotar `toISOString` con "Invalid time value" y se llevaba puesto todo el submit.
   */
  const toIsoDateOrFallback = (value: unknown, fallback = ""): string => {
    if (value instanceof Date) return isNaN(value.getTime()) ? fallback : value.toISOString();
    return (typeof value === "string" && value) || fallback;
  };

  const serializeRetentionTime = (preset: string, custom: { days: number | string; hours: number | string; minutes: number | string }) => {
    if (preset === "30min") return "0d00h30m";
    if (preset === "1h") return "0d01h0m";
    if (preset === "12h") return "0d12h0m";
    if (preset === "24h") return "1d00h0m";
    
    const d = Number(custom.days) || 0;
    const h = Number(custom.hours) || 0;
    const m = Number(custom.minutes) || 0;
    return `${d}d${String(h).padStart(2, "0")}h${m}m`;
  };

  const buildEventPayload = (
    data: EntryRegistrationFormValues,
    eventType: "in" | "out" | "reject" = "in"
  ) => {
    const selectedVehicle =
      data.selectedVehicleId && data.selectedVehicleId !== "new"
        ? (data.associatedVehicles?.find((v: any) => v.id === data.selectedVehicleId)
           || data.associatedVehicles?.find((v: any) => v.plate && v.plate === data.selectedVehicleId))
        : null;

    const isTransport = isTransportData(data);

    const payload: any = {
      token: authStore.token,
      type: eventType,
      who: data.id || "",
      eventId: data.eventId || data.entranceEvent?.eventId || "",
      // Al egresar el transportista deja sus propias observaciones; en ingreso el comentario sigue
      // siendo la nota de portería del bloque de transporte.
      comment:
        (isTransport && eventType !== "out" ? data.transportNotasIngreso : data.observationsText) ||
        "",
      peopleComment: data.peopleComment || "",
      underagedCount: "0",
      vehicleType: resolveVehicleType(data) || (selectedVehicle ? "car" : "none"),
      eventSource: "web",
      hikvisions: data.enabledFacialDevices || [],
      retentionTimeIn: serializeRetentionTime(data.entryPermanenceTimePreset, data.entryPermanenceTimeCustom),
      retentionTimeOut: serializeRetentionTime(data.exitPermanenceTimePreset, data.exitPermanenceTimeCustom),
    };

    if (data.blacklistAuthorization) {
      payload.blacklistAuthorization = data.blacklistAuthorization;
    }

    if (isTransport) {
      payload.pallets = data.transportPallets ? Number(data.transportPallets) : null;
      // El remito se pide en carga y en descarga. Sólo viaja si hay algo que decir: el número, o
      // el null explícito de "No aplica" (que sólo existe en ingreso). El precinto es opcional y
      // por eso nunca viaja vacío.
      if (data.transportRemitoNoAplica) {
        payload.receipt = null;
      } else if (data.transportNumeroRemito) {
        payload.receipt = data.transportNumeroRemito;
      }
      if (data.transportPrecinto) {
        payload.seal = data.transportPrecinto;
      }
      if (data.transportPais) {
        // El formulario tiene un solo par país/provincia: en ingreso (y en rechazo, que también
        // pasa por portería) es la procedencia del camión; en egreso, el destino.
        const location = {
          country: data.transportPais,
          // La provincia sólo se pide para Argentina; en el resto viaja vacía.
          province: data.transportProvincia || "",
        };
        if (eventType === "out") payload.transportDestination = location;
        else payload.transportOrigin = location;
      }
    }

    return payload;
  };

  /**
   * Verify if personal-data fields have changed and, if so, call updateUser
   * before sending the event — mirrors verifyWho in useActionEvent.
   */
  const updateUserIfNeeded = async (data: EntryRegistrationFormValues) => {
    if (!data.id) return;

    const overrides: Record<string, any> = {};

    if (data.firstName && data.firstName !== data.old?.firstname) overrides.firstname = data.firstName;
    if (data.lastName && data.lastName !== data.old?.lastname) overrides.lastname = data.lastName;
    if (data.email && data.email !== data.old?.email) overrides.email = data.email;
    if (data.phone && data.phone !== data.old?.phone) overrides.phone = data.phone;
    if (data.sex && data.sex !== data.old?.sex) {
      overrides.sex = data.sex;
    }

    const currentBirthDate = parseBirthDateValue(data.birthdate);
    const previousBirthDate = parseBirthDateValue(data.old?.birthdate);
    if (currentBirthDate && !areSameCalendarDate(currentBirthDate, previousBirthDate)) {
      overrides.birthdate = formatBirthdateForApi(currentBirthDate);
    }

    // La documentación del chofer (legajo, licencia, ART) es del usuario, no del evento: se
    // persiste acá. Sólo para transportistas — el resto de los flujos no la recolecta. Como el
    // formulario viene prefilleado con lo que ya tenía el usuario, sólo se manda lo que cambió.
    if (isTransportData(data)) {
      if (data.driverLegajoNumber && data.driverLegajoNumber !== data.old?.transportEmployeeNumber) {
        overrides.transportEmployeeNumber = data.driverLegajoNumber;
        // El legajo se guarda por empresa (upsert en transportEmployeeNumbers): el backend
        // requiere transportCompany para saber a cuál corresponde.
        if (data.transportEmpresa?.id) {
          overrides.transportCompany = data.transportEmpresa.id;
        }
      }
      // Las fechas se comparan por día: el formulario tiene un Date y la referencia un DD-MM-YYYY.
      const licenseDate = data.driverLicenseExpirationDate;
      if (
        licenseDate &&
        !areSameCalendarDate(licenseDate, parseApiDate(data.old?.driverLicenseExpirationDate))
      ) {
        overrides.driverLicenseExpirationDate = formatVtvExpirationDateForApi(licenseDate);
      }
      // `artCompany` es el id del desplegable y reemplaza al legacy `artCompanyName`.
      if (data.driverArtCompany && data.driverArtCompany !== data.old?.artCompany) {
        overrides.artCompany = data.driverArtCompany;
      }
      if (data.driverArtPolicyNumber && data.driverArtPolicyNumber !== data.old?.artPolicyNumber) {
        overrides.artPolicyNumber = data.driverArtPolicyNumber;
      }
      const artDate = data.driverArtExpirationDate;
      if (artDate && !areSameCalendarDate(artDate, parseApiDate(data.old?.artExpirationDate))) {
        overrides.artExpirationDate = formatVtvExpirationDateForApi(artDate);
      }
    }

    // Only call updateUser when there are meaningful overrides
    if (Object.keys(overrides).length > 0) {
      try {
        await userReq.updateUser(authStore.token, data.id, overrides);
      } catch (error) {
        console.warn("Error updating user:", error);
      }
    }
  };

  /**
   * El camión se identifica por id, no por patente. Se resuelve, en orden:
   *  1. el vehículo que se acaba de dar de alta en este mismo submit,
   *  2. la selección del formulario, cuando es un id de la lista del chofer,
   *  3. la patente contra esa misma lista — cubre tanto el fallback a patente de
   *     `VehicleContentUserDataForm` como el egreso, donde no hay formulario de vehículo y la
   *     patente sale de la autorización.
   *
   * Devuelve también la patente para poder caer a `vehicle` si nada de eso alcanzó.
   */
  const resolveTransportVehicle = (
    data: EntryRegistrationFormValues,
    createdVehicleId?: string | null
  ): { vehicleId?: string; plate: string } => {
    const known = data.associatedVehicles || [];
    const plate =
      data.vehiclePlate ||
      data.transportAuthPrefill?.vehiclePlate ||
      data.transportAutorizacionEgreso?.vehiclePlate ||
      "";

    if (createdVehicleId) return { vehicleId: createdVehicleId, plate };

    const selected = data.selectedVehicleId;
    if (selected && selected !== "new") {
      const byId = known.find((vehicle: any) => vehicle.id === selected);
      const byIdUsable = usableVehicleId(byId);
      if (byIdUsable) return { vehicleId: byIdUsable, plate: plate || byId?.plate || "" };
      // La selección puede traer una patente en vez de un id (ver el `?? existing.plate` del form).
      const bySelectedPlate = known.find(
        (vehicle: any) => vehicle.plate && vehicle.plate === selected
      );
      const bySelectedPlateUsable = usableVehicleId(bySelectedPlate);
      if (bySelectedPlateUsable) {
        return { vehicleId: bySelectedPlateUsable, plate: plate || bySelectedPlate?.plate || "" };
      }
    }

    const byPlate = plate
      ? known.find((vehicle: any) => vehicle.plate && vehicle.plate === plate)
      : undefined;
    const byPlateUsable = usableVehicleId(byPlate);
    if (byPlateUsable) return { vehicleId: byPlateUsable, plate };

    // Sin id resoluble el camión viaja por patente: la del formulario, o la que quedó guardada en
    // el vehículo que sí estaba seleccionado (el egreso no monta el formulario de vehículo).
    const selectedVehicle =
      known.find((vehicle: any) => vehicle.id === selected) ||
      known.find((vehicle: any) => vehicle.plate && vehicle.plate === selected);
    return { plate: plate || selectedVehicle?.plate || "" };
  };

  /**
   * Verify vehicle data and attach it to the event payload.
   */
  const verifyVehicle = (
    event: any,
    data: EntryRegistrationFormValues,
    createdVehicleId?: string | null
  ) => {
    const updatedEvent = { ...event };

    if (resolveVehicleType(data) !== "car") return updatedEvent;

    if (isTransportData(data)) {
      const { vehicleId, plate } = resolveTransportVehicle(data, createdVehicleId);
      // Uno u otro, nunca los dos: el id es el contrato, la patente sólo el plan B cuando el alta
      // falló o el vehículo no está en la lista del chofer.
      if (vehicleId) updatedEvent.vehicleId = vehicleId;
      else if (plate) updatedEvent.vehicle = plate;
      return updatedEvent;
    }

    if (data.selectedVehicleId && data.selectedVehicleId !== "new") {
      const known = data.associatedVehicles || [];
      const selected =
        known.find((vehicle: any) => vehicle.id === data.selectedVehicleId) ||
        known.find(
          (vehicle: any) => vehicle.plate && vehicle.plate === data.selectedVehicleId
        );
      // `selectedVehicleId` no siempre es un id: en el egreso el vehículo sale del evento de
      // ingreso y se selecciona por patente, o directamente con el placeholder de `buildExitUser`.
      // Mandarlo crudo hacía que el backend rechazara el evento — se veía en el egreso del
      // acompañante, que hereda `vehicleType: "car"` del chofer y por eso pasa por acá.
      const vehicleId = usableVehicleId(selected || { id: data.selectedVehicleId });
      if (vehicleId) updatedEvent.vehicleId = vehicleId;
      else if (selected?.plate) updatedEvent.vehicle = selected.plate;
    }
    if (data.vehiclePlate) {
      updatedEvent.vehicle = data.vehiclePlate;
    }

    return updatedEvent;
  };

  /**
   * Diferencia entre el vehículo guardado y lo que quedó cargado en el formulario. Lo usan tanto
   * `verifyVehicleOverride` (para el evento) como `updateVehicleIfNeeded` (para persistir el
   * cambio), así que la comparación vive en un solo lugar.
   *
   * Devuelve null cuando no aplica o cuando no cambió nada.
   */
  const buildVehicleOverrides = (
    data: EntryRegistrationFormValues,
    vehicleType: string
  ): { oldVehicle: any; overrides: any; plateChanged: boolean } | null => {
    if (vehicleType !== "car") return null;

    const oldVehicle = data.associatedVehicles?.find(
      (vehicle: any) => vehicle.id === data.selectedVehicleId
        || (vehicle.plate && vehicle.plate === data.selectedVehicleId)
    );
    if (!oldVehicle) return null;
    // Sin un id que el backend pueda resolver (el sentinel del egreso, o la patente usada como id)
    // no hay nada que actualizar: `updateVehicle` no acepta ninguno de los dos y el addEvent tiene
    // que caer al plan B de `verifyVehicle` (mandar la patente), no a un override con un id inventado.
    if (!usableVehicleId(oldVehicle)) return null;

    const insuranceDate = toIsoDateOrFallback(
      data.insuranceExpirationDate,
      oldVehicle.insuranceExpirationDate || ""
    );

    const vtvDate = data.vtvExpirationDate
      ? formatVtvExpirationDateForApi(data.vtvExpirationDate)
      : oldVehicle.technicalReviewExpirationDate || "";

    // El vehículo guardado trae la marca como texto: la comparación y el override siguen yendo
    // contra el nombre, y `brandId` sólo se agrega como dato extra para transportistas.
    const { brandId, brand } = resolveBrand(data.vehicleBrand);
    const isTransport = isTransportData(data);

    // La carrocería (`vehicleTypeId`) se pide en cualquier flujo; el seguro (aseguradora + póliza)
    // sigue siendo exclusivo de transportistas, que es el único formulario que lo pide.
    const vehicleTypeChanged = oldVehicle.vehicleType !== data.vehicleTypeId;
    const insuranceInfoChanged =
      isTransport &&
      (oldVehicle.insuranceCompanyId !== data.vehicleInsuranceCompany ||
        oldVehicle.insurancePolicy !== data.vehiclePolicyNumber);

    const plateChanged = oldVehicle.plate !== data.vehiclePlate;

    const changed =
      oldVehicle.brand !== brand ||
      plateChanged ||
      oldVehicle.model !== data.vehicleModel ||
      oldVehicle.color !== data.vehicleColor ||
      oldVehicle.insuranceExpirationDate !== insuranceDate ||
      oldVehicle.technicalReviewExpirationDate !== vtvDate ||
      vehicleTypeChanged ||
      insuranceInfoChanged;

    if (!changed) return null;

    const overrides: any = {
      id: oldVehicle.id,
      plate: data.vehiclePlate || "",
      brand,
      model: data.vehicleModel || "",
      color: data.vehicleColor || "",
      insuranceExpirationDate: insuranceDate,
      technicalReviewExpirationDate: vtvDate,
    };

    if (data.vehicleTypeId) overrides.vehicleType = data.vehicleTypeId;

    if (isTransport) {
      if (brandId) overrides.brandId = brandId;
      if (data.vehicleInsuranceCompany) {
        overrides.insuranceCompanyId = data.vehicleInsuranceCompany;
      }
      if (data.vehiclePolicyNumber) overrides.insurancePolicy = data.vehiclePolicyNumber;
    }

    return { oldVehicle, overrides, plateChanged };
  };

  /**
   * Verify vehicle override data and attach it to the event payload.
   * Mirrors verifyVehicleOverride in useActionEvent.ts.
   *
   * `alreadyUpdated` viaja sólo cuando ya llamamos a updateVehicle, para que el backend no vuelva a
   * aplicar el mismo cambio.
   */
  const verifyVehicleOverride = (
    event: any,
    data: EntryRegistrationFormValues,
    alreadyUpdated = false
  ) => {
    const diff = buildVehicleOverrides(data, event.vehicleType);
    if (!diff) return event;

    return {
      ...event,
      vehicleOverrides: alreadyUpdated
        ? { ...diff.overrides, alreadyUpdated: true }
        : diff.overrides,
    };
  };

  /**
   * Persiste los cambios del vehículo ya registrado con updateVehicle, en cualquier flujo (no sólo
   * transportistas: dejarlo únicamente en `vehicleOverrides` del evento no alcanzaba para que el
   * backend aplicara, por ejemplo, un cambio de `vehicleType` en un vehículo de un visitante).
   *
   * Devuelve si el update llegó a hacerse, que es lo que decide el `alreadyUpdated` del evento.
   */
  const updateVehicleIfNeeded = async (
    data: EntryRegistrationFormValues
  ): Promise<boolean> => {
    const diff = buildVehicleOverrides(data, resolveVehicleType(data));
    if (!diff || !diff.oldVehicle?.id) return false;

    // updateVehicle no acepta patente: si cambió, dejamos que el backend aplique el override entero
    // en vez de mandar un update parcial y marcarlo como ya aplicado.
    if (diff.plateChanged) return false;

    const { overrides } = diff;
    try {
      await vehicleApi.updateVehicle({
        token: authStore.token,
        id: diff.oldVehicle.id,
        vehicleType: overrides.vehicleType,
        brandId: overrides.brandId,
        brand: overrides.brand,
        model: overrides.model,
        color: overrides.color,
        technicalReviewExpirationDate: overrides.technicalReviewExpirationDate,
        insuranceCompanyId: overrides.insuranceCompanyId,
        insuranceCompany: overrides.insuranceCompany,
        insurancePolicy: overrides.insurancePolicy,
        insuranceExpirationDate: overrides.insuranceExpirationDate,
      });
      return true;
    } catch (error) {
      // Si falla, el evento sale con el override sin marcar y lo aplica el backend.
      console.warn("Error updating vehicle:", error);
      return false;
    }
  };

  /**
   * Rehace la confirmación en guardia con la que entró el usuario para poder mandarla en el egreso.
   * El tipo de invitado y la transportista salen de la confirmación del ingreso y no del formulario:
   * el acompañante del transportista entró con `guestsType: "transport"` + `transportCompany`, y su
   * egreso arranca como "Visitante" (ver determineGuestTypeFromEvent), así que tomar el tipo del
   * formulario le cambiaba la confirmación al salir.
   *
   * Devuelve null cuando la confirmación no tiene anfitrión: no es una autorización que el backend
   * pueda resolver y reenviarla hace fallar el egreso. Es el caso del acompañante del transportista
   * cuando la autorización del chofer no tiene una persona asociada (ver
   * resolveTransportConfirmationSource). Excepción: `allowNullHost`, para cuando el propio egreso es
   * el de ese acompañante — ahí el backend acepta host: null (mismo caso que
   * resolveTransportConfirmationSource ya manda tal cual en processCompanions), así que sí se
   * reenvía (ver buildDefaultEntryRegistrationValues, que la prefillea con el mismo criterio).
   */
  const buildExitEmployeeConfirmation = (
    invite: any,
    fallbackGuestsType: string,
    allowNullHost = false
  ) => {
    const host = invite.host?.id || invite.host;
    if (!host && !allowNullHost) return null;

    const confirmation: any = {
      host: host || null,
      company: invite.company || "",
      guestsType: invite.guestsType || fallbackGuestsType,
    };
    const transportCompany = invite.transportCompany?.id || invite.transportCompany;
    if (transportCompany) confirmation.transportCompany = transportCompany;
    // Sin anfitrión, el responsable que autorizó el acceso y el medio por el que se consiguió son
    // lo único que respalda la confirmación: se reenvían tal cual entró (autorización aprobada por
    // el guardia).
    if (!host && invite.authorizedBy) confirmation.authorizedBy = invite.authorizedBy;
    if (!host && invite.communicationMethod) {
      confirmation.communicationMethod = invite.communicationMethod;
    }
    return confirmation;
  };

  // /**
  //  * Verify authorizations and attach them to the event payload.
  //  */
  const verifyAuthorizations = (
    event: any,
    data: EntryRegistrationFormValues,
    eventType: "in" | "out" | "reject" = "in"
  ) => {
    let updatedEvent = { ...event };

    if (data.isEmployee) {
      return updatedEvent;
    }

    const guestTypeValue =
      data.selectedAccessMethod === "contractor"
        ? "contractor"
        : data.selectedAccessMethod === "eventual"
        ? "eventual"
        : "any";

    if (eventType === "out" && data.entranceEvent) {
      // The event object might be nested inside `entranceEvent.event` as per API response
      const ent = data.entranceEvent.event || data.entranceEvent;
      // El acompañante del transportista egresa sin anfitrión propio: el backend acepta ese null
      // sólo para él (ver buildExitEmployeeConfirmation).
      const allowNullHost = isTransportCompanionEvent(ent);

      const exitInvites: string[] = [];
      const exitPrevals: string[] = [];
      const exitEmployeeConfirmations: any[] = [];

      // Classify each invite from the entrance event by its type
      (ent.invites || []).forEach((i: any) => {
        if (i.type === "guard") {
          const confirmation = buildExitEmployeeConfirmation(i, guestTypeValue, allowNullHost);
          if (confirmation) exitEmployeeConfirmations.push(confirmation);
        } else if (i.type === "contractor" || i.type === "eventual") {
          exitPrevals.push(i.id || i);
        } else {
          // "invite" or any other type
          exitInvites.push(i.id || i);
        }
      });

      // Lo que el guardia elige en esta pantalla se suma a lo que venía del ingreso, no lo
      // reemplaza: el egreso del acompañante del transportista llega sin una confirmación
      // reutilizable y el guardia resuelve buscando un empleado en "Registrar egreso manual".
      // Antes esa selección se perdía, porque el egreso se armaba sólo con el evento de ingreso.
      const mergeIds = (fromEntrance: string[], fromForm?: any[]) => {
        const ids = new Set(fromEntrance);
        (fromForm || []).filter(Boolean).forEach((id: string) => ids.add(id));
        return Array.from(ids);
      };

      const invites = mergeIds(exitInvites, data.invites);
      const prevalidations = mergeIds(exitPrevals, data.prevalidation);
      // El ingreso no clasifica `authorizations` (sólo invites y prevalidaciones), así que las del
      // egreso salen enteras del formulario, que ya viene prefilleado con las del evento de ingreso.
      const authorizations = (data.authorizations || []).filter(Boolean);

      const employeeConfirmations = [...exitEmployeeConfirmations];
      (data.employeeConfirmations || [])
        .filter((ec: any) => ec && ec.host)
        .forEach((ec: any) => {
          const alreadySent = employeeConfirmations.some(
            (c: any) => c.host === ec.host && c.company === ec.company
          );
          if (alreadySent) return;
          employeeConfirmations.push({ ...ec, guestsType: ec.guestsType || guestTypeValue });
        });

      if (invites.length > 0) updatedEvent.invites = invites;
      if (prevalidations.length > 0) updatedEvent.prevalidations = prevalidations;
      if (authorizations.length > 0) updatedEvent.authorizations = authorizations;
      if (employeeConfirmations.length > 0) {
        updatedEvent.employeeConfirmations = employeeConfirmations;
      }

      return updatedEvent;
    }

    if (data.invites && data.invites.length > 0 && !!data.invites[0]) {
      updatedEvent.invites = data.invites;
    }
    if (data.authorizations && data.authorizations.length > 0 && !!data.authorizations[0]) {
      updatedEvent.authorizations = data.authorizations;
    }
    if (data.prevalidation && data.prevalidation.length > 0 && !!data.prevalidation[0]) {
      updatedEvent.prevalidations = data.prevalidation;
    }

    if (
      data.employeeConfirmations &&
      data.employeeConfirmations.length > 0 &&
      !!data.employeeConfirmations[0]
    ) {
      updatedEvent.employeeConfirmations = data.employeeConfirmations.map((ec: any) => ({
        ...ec,
        guestsType: guestTypeValue,
      }));
    }

    return updatedEvent;
  };

  // ---------------------------------------------------------------------------
  // Vehicle creation — mirrors useVehicle.ts → addNewVehicle / addVehicleImage
  // ---------------------------------------------------------------------------

  /**
   * Convert a File object to a DataURL string, which is the format
   * expected by the SDK upload methods (uploadImgVehicle, uploadImgEvent).
   */
  const fileToDataURL = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  /**
   * Upload a vehicle image using the uploadVehicle endpoint.
   * Mirrors useVehicle.ts → addVehicleImage.
   *
   * @param vehicleId  – the id returned from addVehicle (or existing vehicle id)
   * @param file       – DataURL string
   * @param fileType   – e.g. "insurancePhoto"
   * @param fileName   – file name for the upload
   */
  const uploadVehicleImage = async (
    vehicleId: string,
    file: string,
    fileType: VehicleFileType,
    fileName: string,
    throwOnError = false
  ) => {
    try {
      await userReq.uploadImgVehicle(authStore.token, file, vehicleId, fileType, fileName);
    } catch (error) {
      console.warn(`Error uploading vehicle image (${fileType}):`, error);
      if (throwOnError) throw error;
    }
  };

  /**
   * Upload vehicle images for an EXISTING vehicle (selectedVehicleId !== "new").
   * Mirrors NewVehicle.tsx → add() when vehicle.id exists:
   *   if (vehicleModified.insurancePhotoNew)
   *     addVehicleImage(vehicle.id, vehicleModified.insurancePhotoNew);
   */
  const uploadExistingVehicleImages = async (
    data: EntryRegistrationFormValues,
    throwOnError = false
  ) => {
    if (
      !data.selectedVehicleId ||
      data.selectedVehicleId === "new" ||
      resolveVehicleType(data) !== "car"
    )
      return;

    const vehicleId = data.selectedVehicleId;

    // Insurance photo – same as addVehicleImage in useVehicle.ts
    if (data.insurancePhoto && data.insurancePhoto instanceof File) {
      const dataUrl = await fileToDataURL(data.insurancePhoto);
      await uploadVehicleImage(
        vehicleId,
        dataUrl,
        "insurancePhoto",
        fileNames.INSURANCE_FILENAME,
        throwOnError
      );
    }

    // VTV (technical review) photo
    if (data.vtvPhoto && data.vtvPhoto instanceof File) {
      const dataUrl = await fileToDataURL(data.vtvPhoto);
      await uploadVehicleImage(
        vehicleId,
        dataUrl,
        "technicalReviewPhoto1",
        fileNames.TECHNICAL_REVIEW_PHOTO_1_FILENAME,
        throwOnError
      );
    }
  };

  /** Persiste el vehículo existente al confirmar "Guardar". Las altas nuevas siguen en el
   * submit final porque todavía no tienen un id para `uploadVehicle`. */
  const saveExistingVehicle = async (data: EntryRegistrationFormValues) => {
    const oldVehicle = data.associatedVehicles?.find(
      (vehicle: any) => vehicle.id === data.selectedVehicleId
        || (vehicle.plate && vehicle.plate === data.selectedVehicleId)
    );
    if (!oldVehicle?.id) {
      // El vehículo principal puede venir autocompletado antes de que su ficha esté disponible en
      // `associatedVehicles`. No es un error de carga: se conserva el formulario y el submit final
      // mantiene el flujo histórico para intentar persistirlo cuando ya esté resuelto.
      return { success: true, deferred: true };
    }

    const diff = buildVehicleOverrides(data, resolveVehicleType(data));
    if (diff?.plateChanged) {
      return {
        success: false,
        error: "La patente de un vehículo existente no se puede modificar desde este formulario.",
      };
    }

    try {
      // El objeto que devolvemos es el que el caller usa para refrescar su copia en
      // `associatedVehicles` — sin esto, la tarjeta seguía mostrando los datos de antes del guardado
      // porque nadie actualizaba la entrada cacheada.
      let updatedVehicle = oldVehicle;
      if (diff) {
        const { overrides } = diff;
        await vehicleApi.updateVehicle({
          token: authStore.token,
          id: oldVehicle.id,
          vehicleType: overrides.vehicleType,
          brandId: overrides.brandId,
          brand: overrides.brand,
          model: overrides.model,
          color: overrides.color,
          technicalReviewExpirationDate: overrides.technicalReviewExpirationDate,
          insuranceCompanyId: overrides.insuranceCompanyId,
          insuranceCompany: overrides.insuranceCompany,
          insurancePolicy: overrides.insurancePolicy,
          insuranceExpirationDate: overrides.insuranceExpirationDate,
        });
        updatedVehicle = { ...oldVehicle, ...overrides };
      }

      // Acá propagamos el error al formulario para que el guardia pueda reintentar antes de
      // continuar. En el submit final se conserva el comportamiento tolerante previo.
      await uploadExistingVehicleImages(data, true);
      return { success: true, vehicle: updatedVehicle };
    } catch (error) {
      console.warn("Error saving existing vehicle:", error);
      const message =
        typeof error === "string" ? error : (error as any)?.message || (error as any)?.msg;
      return {
        success: false,
        error: typeof message === "string" ? message : "No se pudo guardar el vehículo.",
      };
    }
  };

  /**
   * Fotos de la documentación del chofer. Van al usuario (uploadUser), no al evento: son parte de
   * su legajo y se reutilizan en el próximo ingreso. Sólo transportistas.
   */
  const uploadDriverDocumentsIfNeeded = async (data: EntryRegistrationFormValues) => {
    if (!isTransportData(data) || !data.id) return;

    const documents: Array<{ file: unknown; fileType: string; fileName: string }> = [
      {
        file: data.driverLicensePhoto,
        fileType: "frontDriverLicense",
        fileName: fileNames.FRONT_LICENSE_FILENAME,
      },
      {
        file: data.driverArtPhoto,
        fileType: "artPhoto1",
        fileName: fileNames.ART_PHOTO_1_FILENAME,
      },
    ];

    for (const { file, fileType, fileName } of documents) {
      // Un valor string es una foto ya guardada: volver a subirla la duplicaría.
      if (!(file instanceof File)) continue;
      try {
        const dataUrl = await fileToDataURL(file);
        await userReq.uploadImgUser(authStore.token, dataUrl, data.id, fileType, fileName);
      } catch (error) {
        console.warn(`Error uploading driver document (${fileType}):`, error);
      }
    }
  };

  /**
   * Foto de la bici: nunca hay un `Vehicle` registrado detrás (las bicis no pasan por
   * `addVehicle`), así que es una foto del evento — va por `uploadImgEvent`/`ws-security/upload`,
   * no por `uploadVehicle` (que ya no acepta el `fileType: "photo"` que se usaba acá antes).
   */
  const uploadBikePhotoIfNeeded = async (data: EntryRegistrationFormValues, eventId?: string) => {
    if (data.vehicleType !== "bike" || !data.accessVehicleBikePhoto) return;
    if (!(data.accessVehicleBikePhoto instanceof File)) return;

    const targetEventId = eventId || data.eventId;
    if (!targetEventId) return;

    try {
      const dataUrl = await fileToDataURL(data.accessVehicleBikePhoto);
      await userReq.uploadImgEvent(authStore.token, dataUrl, targetEventId, "bikePhoto", "bike_photo.jpg");
    } catch (error) {
      console.warn("Error uploading bike photo:", error);
    }
  };

  /**
   * Create a new vehicle via the addVehicle endpoint and upload associated
   * images when present. Returns the newly created vehicle id or null.
   *
   * Mirrors useVehicle.ts → addNewVehicle:
   *   1. vehicleApi.addVehicle(...)
   *   2. if (response.status === "ok" && newVehicle.insurancePhotoNew)
   *        addVehicleImage(response.data.id, newVehicle.insurancePhotoNew)
   */
  const addVehicleIfNeeded = async (data: EntryRegistrationFormValues): Promise<string | null> => {
    if (resolveVehicleType(data) !== "car" || !data.id) return null;

    // Only create when the user filled the vehicle form fields
    if (!data.vehiclePlate) return null;

    try {
      const isTransport = isTransportData(data);

      // En transportistas el vencimiento del seguro viaja como DD-MM-YYYY; el resto de los flujos
      // sigue mandando el ISO de siempre.
      const insuranceDate = isTransport
        ? formatVtvExpirationDateForApi(data.insuranceExpirationDate)
        : toIsoDateOrFallback(data.insuranceExpirationDate);

      // El transportista puede marcar que la VTV no aplica; en ese caso no se manda vencimiento.
      const vtvDate =
        isTransport && data.vehicleAppliesVtv === false
          ? ""
          : formatVtvExpirationDateForApi(data.vtvExpirationDate);
      const { brandId, brand } = resolveBrand(data.vehicleBrand);

      const response = await vehicleApi.addVehicle({
        token: authStore.token,
        user: data.id,
        plate: data.vehiclePlate,
        // `brand` (texto libre) se sigue mandando siempre; `brandId` sólo en transportistas, que es
        // donde el backend ya acepta el catálogo nuevo.
        brand,
        model: data.vehicleModel || "",
        color: data.vehicleColor || "",
        insuranceExpirationDate: insuranceDate,
        technicalReviewExpirationDate: vtvDate,
        // La carrocería (`vehicleTypeId`) se pide en cualquier flujo, no sólo transportistas.
        ...(data.vehicleTypeId ? { vehicleType: data.vehicleTypeId } : {}),
        ...(isTransport
          ? {
              brandId,
              insuranceCompanyId: data.vehicleInsuranceCompany,
              insurancePolicy: data.vehiclePolicyNumber,
            }
          : {}),
      });

      if (response?.status === "ok" || response?.data?.id) {
        const newVehicleId: string = response.data.id;

        // Upload insurance photo – mirrors useVehicle.ts:
        //   if (newVehicle.insurancePhotoNew)
        //     addVehicleImage(response.data.id, newVehicle.insurancePhotoNew);
        if (data.insurancePhoto && data.insurancePhoto instanceof File) {
          const dataUrl = await fileToDataURL(data.insurancePhoto);
          await uploadVehicleImage(
            newVehicleId,
            dataUrl,
            "insurancePhoto",
            fileNames.INSURANCE_FILENAME
          );
        }

        // Upload VTV (technical review) photo
        if (data.vtvPhoto && data.vtvPhoto instanceof File) {
          const dataUrl = await fileToDataURL(data.vtvPhoto);
          await uploadVehicleImage(
            newVehicleId,
            dataUrl,
            "technicalReviewPhoto1",
            fileNames.TECHNICAL_REVIEW_PHOTO_1_FILENAME
          );
        }

        return newVehicleId;
      }
    } catch (error) {
      console.warn("Error adding vehicle:", error);
    }

    return null;
  };

  /**
   * Resuelve cada acoplado cargado en "Vehículos Asociados" a un id real de `Vehicle`, creándolo
   * con el mismo endpoint que el vehículo principal (`addVehicle`) cuando no matchea ninguno de los
   * ya registrados del chofer. Devuelve los ids para mandarlos como `trailers` en el addEvent.
   */
  /** Mismo diff que `buildVehicleOverrides`, pero para un acoplado: sus campos en el formulario usan
   * otros nombres (`insuranceCompany`/`policyNumber` en vez de `vehicleInsuranceCompany`/
   * `vehiclePolicyNumber`) y siempre son de transportistas. A diferencia del vehículo principal, acá
   * no hay un `vehicleOverrides` embebido en el evento que sirva de respaldo si esto falla: el
   * `addEvent` de acoplados sólo manda ids (`userEvent.trailers`), así que este `updateVehicle` es
   * la única forma de persistir lo que el guardia haya editado. Devuelve null si no cambió nada, o
   * si la patente cambió (`updateVehicle` no la acepta; preferimos no actualizar antes que hacerlo
   * contra el vehículo equivocado). */
  const buildTrailerVehicleUpdate = (trailer: any, oldVehicle: any): Record<string, any> | null => {
    if (oldVehicle.plate !== trailer.vehiclePlate) return null;

    // Los acoplados son exclusivamente de transporte. Igual que en `addVehicle`, el backend
    // espera el vencimiento del seguro en DD-MM-YYYY, no el ISO que usan otros flujos.
    const insuranceDate = trailer.insuranceExpirationDate
      ? formatVtvExpirationDateForApi(trailer.insuranceExpirationDate)
      : oldVehicle.insuranceExpirationDate || "";
    const vtvDate = trailer.vtvExpirationDate
      ? formatVtvExpirationDateForApi(trailer.vtvExpirationDate)
      : oldVehicle.technicalReviewExpirationDate || "";
    const { brandId, brand } = resolveBrand(trailer.vehicleBrand);

    const changed =
      oldVehicle.brand !== brand ||
      oldVehicle.color !== (trailer.vehicleColor || "") ||
      oldVehicle.insuranceExpirationDate !== insuranceDate ||
      oldVehicle.technicalReviewExpirationDate !== vtvDate ||
      oldVehicle.vehicleType !== trailer.vehicleTypeId ||
      oldVehicle.insuranceCompanyId !== trailer.insuranceCompany ||
      oldVehicle.insurancePolicy !== trailer.policyNumber;

    if (!changed) return null;

    const overrides: Record<string, any> = {
      token: authStore.token,
      id: oldVehicle.id,
      brand,
      color: trailer.vehicleColor || "",
      insuranceExpirationDate: insuranceDate,
      technicalReviewExpirationDate: vtvDate,
    };
    if (brandId) overrides.brandId = brandId;
    if (trailer.vehicleTypeId) overrides.vehicleType = trailer.vehicleTypeId;
    if (trailer.insuranceCompany) overrides.insuranceCompanyId = trailer.insuranceCompany;
    if (trailer.policyNumber) overrides.insurancePolicy = trailer.policyNumber;

    return overrides;
  };

  const updateTrailerVehicleIfNeeded = async (trailer: any, oldVehicle: any) => {
    const overrides = buildTrailerVehicleUpdate(trailer, oldVehicle);
    if (!overrides) return;
    try {
      await vehicleApi.updateVehicle(overrides as any);
    } catch (error) {
      console.warn("Error updating trailer vehicle:", error);
    }
  };

  /** Mismas fotos que el vehículo principal (`addVehicleIfNeeded` / `uploadExistingVehicleImages`),
   * pero para un acoplado: sirve tanto al crearlo como si ya tenía id y el guardia le cargó una
   * foto nueva al editarlo. */
  const uploadTrailerVehicleImages = async (
    vehicleId: string,
    trailer: any,
    throwOnError = false
  ) => {
    if (trailer.insurancePhoto instanceof File) {
      const dataUrl = await fileToDataURL(trailer.insurancePhoto);
      await uploadVehicleImage(
        vehicleId,
        dataUrl,
        "insurancePhoto",
        fileNames.INSURANCE_FILENAME,
        throwOnError
      );
    }
    if (trailer.vtvPhoto instanceof File) {
      const dataUrl = await fileToDataURL(trailer.vtvPhoto);
      await uploadVehicleImage(
        vehicleId,
        dataUrl,
        "technicalReviewPhoto1",
        fileNames.TECHNICAL_REVIEW_PHOTO_1_FILENAME,
        throwOnError
      );
    }
  };

  /** Equivalente a `saveExistingVehicle` para un acoplado ya registrado. */
  const saveExistingTrailerVehicle = async (trailer: any, oldVehicle: any) => {
    if (!oldVehicle?.id) {
      return { success: false, error: "No se encontró el vehículo asociado a actualizar." };
    }
    if (oldVehicle.plate !== trailer.vehiclePlate) {
      return {
        success: false,
        error: "La patente de un vehículo asociado existente no se puede modificar.",
      };
    }

    try {
      const overrides = buildTrailerVehicleUpdate(trailer, oldVehicle);
      let updatedVehicle = oldVehicle;
      if (overrides) {
        await vehicleApi.updateVehicle(overrides as any);
        // `overrides` es el payload crudo (incluye `token`): no queremos eso en la copia cacheada.
        const { token: _token, ...vehicleFields } = overrides;
        updatedVehicle = { ...oldVehicle, ...vehicleFields };
      }
      await uploadTrailerVehicleImages(oldVehicle.id, trailer, true);
      return { success: true, vehicle: updatedVehicle };
    } catch (error) {
      console.warn("Error saving existing trailer vehicle:", error);
      const message =
        typeof error === "string" ? error : (error as any)?.message || (error as any)?.msg;
      return {
        success: false,
        error: typeof message === "string" ? message : "No se pudo guardar el vehículo asociado.",
      };
    }
  };

  const resolveTrailerVehicles = async (data: EntryRegistrationFormValues): Promise<string[]> => {
    if (!isTransportData(data) || !data.id) return [];

    const trailers = data.trailerVehicles || [];
    const knownVehicles = data.associatedVehicles || [];
    const ids: string[] = [];

    for (const trailer of trailers) {
      if (trailer.id) {
        const oldVehicle = knownVehicles.find((vehicle: any) => vehicle.id === trailer.id);
        if (oldVehicle) await updateTrailerVehicleIfNeeded(trailer, oldVehicle);
        await uploadTrailerVehicleImages(trailer.id, trailer);
        ids.push(trailer.id);
        continue;
      }

      if (!trailer.vehiclePlate) continue;

      // Puede que el chofer ya tenga registrado ese vehículo aunque el guardia lo haya tipeado a
      // mano (p.ej. el acoplado que viene de la autorización, que sólo trae la patente).
      const existing = knownVehicles.find((vehicle: any) => vehicle.plate === trailer.vehiclePlate);
      if (existing?.id) {
        await updateTrailerVehicleIfNeeded(trailer, existing);
        await uploadTrailerVehicleImages(existing.id, trailer);
        ids.push(existing.id);
        continue;
      }

      try {
        // Los acoplados son sólo de transportistas: siempre DD-MM-YYYY.
        const insuranceDate = formatVtvExpirationDateForApi(trailer.insuranceExpirationDate);
        const vtvDate =
          trailer.appliesVtv === false ? "" : formatVtvExpirationDateForApi(trailer.vtvExpirationDate);
        const { brandId, brand } = resolveBrand(trailer.vehicleBrand);

        const response = await vehicleApi.addVehicle({
          token: authStore.token,
          user: data.id,
          plate: trailer.vehiclePlate,
          brand,
          brandId,
          model: "",
          color: trailer.vehicleColor || "",
          insuranceExpirationDate: insuranceDate,
          technicalReviewExpirationDate: vtvDate,
          insuranceCompanyId: trailer.insuranceCompany,
          insurancePolicy: trailer.policyNumber,
          // Misma carrocería del catálogo que manda el vehículo principal.
          ...(trailer.vehicleTypeId ? { vehicleType: trailer.vehicleTypeId } : {}),
        });

        const newId = response?.data?.id;
        if (newId) {
          await uploadTrailerVehicleImages(newId, trailer);
          ids.push(newId);
        }
      } catch (error) {
        console.warn("Error adding trailer vehicle:", error);
      }
    }

    return ids;
  };

  // ---------------------------------------------------------------------------
  // Transporte — autorizaciones y controles de portería
  // ---------------------------------------------------------------------------

  const CONTROL_PHOTO_FILE_TYPES: Record<string, string> = {
    receipt: "receiptPhoto",
    seal: "sealPhoto",
    vehicleCustody: "vehicleCustodyPhoto",
    satelliteCustody: "satelliteCustodyPhoto",
    vehiclePlate: "vehiclePlatePhoto",
    trailerPlate: "trailerPlatePhoto",
  };

  /**
   * Sube la foto de un control y devuelve el id con el que referenciarla. Si la respuesta no trae
   * un id usable devuelve null: preferimos mandar el control sin foto antes que un formato inventado.
   */
  const uploadControlPhoto = async (
    eventId: string,
    photo: File,
    fileType: string,
    fileName: string
  ): Promise<string | null> => {
    try {
      const dataUrl = await fileToDataURL(photo);
      const response: any = await userReq.uploadImgEvent(
        authStore.token,
        dataUrl,
        eventId,
        fileType,
        fileName
      );
      const id = response?.data?.id || response?.id || response?.data;
      return typeof id === "string" ? id : null;
    } catch (error) {
      console.warn(`Error uploading control photo (${fileType}):`, error);
      return null;
    }
  };

  /** Índice de una fila de acoplado (`trailerPlate` = 1, `trailerPlate-2` = 2, …), o null si la
   * clave no es de acoplado. */
  const trailerPlateIndex = (key: GateControlKey): number | null => {
    if (key === "trailerPlate") return 1;
    const match = /^trailerPlate-(\d+)$/.exec(key);
    return match ? Number(match[1]) : null;
  };

  /**
   * `transportAuthorizations` del evento.
   *
   * En entrada el camión no entra con la preautorización sino con el pedido que la referencia, así
   * que sólo viaja una fila `kind === "request"` — que, tras `mergeLocalDockRequests`, ya es el
   * pedido de dock cuando lo hubo. En salida viaja la autorización de egreso con los controles que
   * verificó el guardia.
   */
  const verifyTransportAuthorizations = async (
    event: any,
    data: EntryRegistrationFormValues,
    eventType: "in" | "out" | "reject"
  ) => {
    if (!isTransportData(data)) return event;

    if (eventType === "out") {
      const exitAuth = data.transportAutorizacionEgreso;
      if (!exitAuth?.id) return event;

      const controls = data.transportExitControls || {};
      const control: Record<string, any> = {};

      for (const key of EXIT_CONTROL_KEYS) {
        const item = controls[key];
        if (!item) continue;

        const entry: Record<string, any> = {
          verified: !!item.verified,
          observations: item.observations || "",
        };
        if (item.photo instanceof File && event.eventId) {
          const photoId = await uploadControlPhoto(
            event.eventId,
            item.photo,
            CONTROL_PHOTO_FILE_TYPES[key],
            `${key}_photo.jpg`
          );
          if (photoId) entry.photo = photoId;
        }
        control[key] = entry;
      }

      const vehiclePlateItem = controls.vehiclePlate;
      if (vehiclePlateItem) {
        const entry: Record<string, any> = {
          verified: !!vehiclePlateItem.verified,
          observations: vehiclePlateItem.observations || "",
        };
        if (vehiclePlateItem.photo instanceof File && event.eventId) {
          const photoId = await uploadControlPhoto(
            event.eventId,
            vehiclePlateItem.photo,
            CONTROL_PHOTO_FILE_TYPES.vehiclePlate,
            "vehiclePlate_photo.jpg"
          );
          if (photoId) entry.photo = photoId;
        }
        control.vehiclePlate = entry;
      }

      const trailerPlateKeys = (Object.keys(controls) as GateControlKey[])
        .map((key) => ({ key, index: trailerPlateIndex(key) }))
        .filter((entry): entry is { key: GateControlKey; index: number } => entry.index !== null)
        .sort((a, b) => a.index - b.index);

      if (trailerPlateKeys.length > 0) {
        const trailerPlates: Record<string, any>[] = [];
        for (const { key, index } of trailerPlateKeys) {
          const item = controls[key];
          if (!item) continue;

          const entry: Record<string, any> = {
            plate: item.plate || "",
            verified: !!item.verified,
            observations: item.observations || "",
          };
          if (item.photo instanceof File && event.eventId) {
            const fileType =
              index === 1
                ? CONTROL_PHOTO_FILE_TYPES.trailerPlate
                : `${CONTROL_PHOTO_FILE_TYPES.trailerPlate}${index}`;
            const photoId = await uploadControlPhoto(
              event.eventId,
              item.photo,
              fileType,
              index === 1 ? "trailerPlate_photo.jpg" : `trailerPlate${index}_photo.jpg`
            );
            if (photoId) entry.photo = photoId;
          }
          trailerPlates.push(entry);
        }
        control.trailerPlates = trailerPlates;
      }

      return {
        ...event,
        transportAuthorizations: [
          Object.keys(control).length > 0 ? { id: exitAuth.id, control } : { id: exitAuth.id },
        ],
      };
    }

    const row = data.transportAutorizacionFull?.rows?.[0];
    // Una preautorización sin pedido hijo no sirve para ingresar: el formulario lo bloquea antes
    // (ver la regla de `transportAuthorizationSelected`), acá sólo evitamos mandar su id.
    if (!row || row.kind !== "request") return event;

    return { ...event, transportAuthorizations: [{ id: row.id }] };
  };

  /**
   * Empresa autorizante y anfitrión de la autorización con la que entra (o sale) el chofer. El
   * acompañante no va en la autorización, así que hereda ese anfitrión como confirmación en
   * guardia: por transitividad también lo autoriza a él.
   *
   * Se lee la misma autorización que manda `verifyTransportAuthorizations`, para que chofer y
   * acompañante queden colgados del mismo anfitrión. `host` es null cuando no hay una persona
   * resoluble (p. ej. autorización todavía pendiente, o aprobada por el guardia).
   *
   * Cuando la autorización la aprobó el guardia por otro medio (self-approve) no hay manager que
   * la respalde: el responsable que autorizó el acceso quedó como texto libre en `authorizedBy`, y
   * es lo que el acompañante hereda en lugar del anfitrión.
   *
   * `dock` es el asignado al chofer para esta autorización: el acompañante lo hereda igual que la
   * empresa y el anfitrión. No aplica en egreso (`ExitAuthorization` no lo trae).
   */
  const resolveTransportConfirmationSource = (
    data: EntryRegistrationFormValues,
    eventType: "in" | "out" | "reject"
  ): {
    company: string;
    host: string | null;
    authorizedBy: string | null;
    communicationMethod: CommunicationMethod | null;
    dock: string | number | null;
  } => {
    if (eventType === "out") {
      const exitAuth = data.transportAutorizacionEgreso;
      return {
        company: exitAuth?.companyId || "",
        host: exitAuth?.people?.[0]?.user || null,
        authorizedBy: exitAuth?.guardApproval?.authorizedBy || null,
        communicationMethod: exitAuth?.guardApproval?.communicationMethod || null,
        dock: null,
      };
    }

    const group = data.transportAutorizacionFull;
    const row = group?.rows?.[0];
    return {
      company: group?.companyId || "",
      host: row?.people?.[0]?.user || null,
      authorizedBy: row?.guardApproval?.authorizedBy || null,
      communicationMethod: row?.guardApproval?.communicationMethod || null,
      dock: row?.dock ?? group?.dock ?? null,
    };
  };

  // ---------------------------------------------------------------------------
  // Companion event processing — mirrors useActionEvent.ts → sendEvent
  // ---------------------------------------------------------------------------

  /**
   * For each companion in the form:
   *  1. findUserByDNI to resolve their backend id
   *  2. updateUser to persist any name / field changes entered in the form
   *  3. For "in" events: createInitialEvent to obtain a companionEventId
   *     For "out" events: use getLastEntrance to find the existing entrance eventId
   *  4. fire addEventAll with the SAME payload as the main event, only
   *     overriding `who` / `eventId` and adding `mainEvent` / `driver`
   *
   * @param data           – full form values
   * @param mainEventPayload – the already-built main-user event (authorizations,
   *                           vehicle, employeeConfirmations, etc. are copied)
   * @param eventType      – "in", "out", or "reject"
   */
  const processCompanions = async (
    data: EntryRegistrationFormValues,
    mainEventPayload: any,
    eventType: "in" | "out" | "reject" = "in",
    fullAuthorizations: any[] = []
  ) => {
    // Un acompañante recién agregado vive en `companions` desde el scan, para que sus campos
    // puedan bindearse por `name` (companions.${index}.*) mientras se lo edita — pero eso no
    // significa que el guardia terminó de cargarlo. Sólo lo confirma "Guardar" en su subformulario
    // (ver VehicleContentCompanionDataForm → handleSave): sin eso no se manda al backend, aunque
    // el guardia haya llegado a completar algunos campos y después abandonara la edición.
    const confirmedCompanions = (data.companions || []).filter(
      (companion: any) => companion?.companionConfirmed
    );
    if (confirmedCompanions.length === 0) return;

    const isTransport = isTransportData(data);

    // Build companion employee confirmations based on main user's authorizations
    const companionConfirmations: any[] = [];

    if (data.isEmployee && data.employeeWorkplaces && data.employeeWorkplaces.length > 0) {
      // Driver is employee — inherit from their workplaces
      data.employeeWorkplaces.forEach((wp: any) => {
        const managerId =
          typeof wp.manager === "object" ? wp.manager?.id || wp.manager : wp.manager;
        if (managerId) {
          companionConfirmations.push({
            host: managerId,
            company: wp.company || "",
          });
        }
      });
    } else if (mainEventPayload.employeeConfirmations?.length > 0) {
      // 1. If main event has employeeConfirmations, use them
      companionConfirmations.push(...mainEventPayload.employeeConfirmations);
    } else if (mainEventPayload.hostConfirmations?.length > 0) {
      companionConfirmations.push(
        ...mainEventPayload.hostConfirmations.map((h: any) => ({
          host: h.host || h.id || h,
          company: h.company || mainEventPayload.company || "",
        }))
      );
    } else {
      // 2. Destructure other authorization types into employeeConfirmations
      const mainAuthIds = [
        ...(mainEventPayload.invites || []),
        ...(mainEventPayload.authorizations || []),
        ...(mainEventPayload.prevalidations || []),
      ];

      mainAuthIds.forEach((id) => {
        const auth = fullAuthorizations.find((a) => a.id === id);
        if (auth) {
          const hostId = auth.host?.id || auth.manager?.id || auth.host || auth.manager;
          if (hostId) {
            companionConfirmations.push({
              host: hostId,
              company: auth.company || "",
            });
          }
        }
      });
    }

    await Promise.all(
      confirmedCompanions.map(async (companion: any) => {
        try {
          // 1. Resolve companion's backend id via findUserByDNI
          const countryCode = companion.companionDocumentCountry || data.documentCountry || "";
          const dni = companion.companionDocumentNumber || "";

          if (!dni) {
            console.warn("Companion skipped – no document number");
            return;
          }

          const userResponse = await userReq.findUserByDNI(authStore.token, dni, countryCode);
          const companionUser = userResponse?.data as any;

          const companionId = companionUser?.id as string | undefined;
          if (!companionId) {
            console.warn("Companion skipped – could not resolve user id for DNI:", dni);
            return;
          }

          // 2. Update companion user data so name changes and company are persisted
          const company = companion.isEmployee
            ? companion.employeeWorkplaces?.[0]?.company || ""
            : companion.authorizationCompany || companion.employeeConfirmations?.[0]?.company || "";
          const companionOverrides: Record<string, any> = {};
          if (companion.companionName && companion.companionName !== companionUser.firstname) {
            companionOverrides.firstname = companion.companionName;
          }
          if (companion.companionLastName && companion.companionLastName !== companionUser.lastname) {
            companionOverrides.lastname = companion.companionLastName;
          }
          if (companion.companionEmail && companion.companionEmail !== companionUser.email) {
            companionOverrides.email = companion.companionEmail;
          }
          if (companion.companionCellphone && companion.companionCellphone !== companionUser.phone) {
            companionOverrides.phone = companion.companionCellphone;
          }
          if (companion.companionSex && companion.companionSex !== companionUser.sex) {
            companionOverrides.sex = companion.companionSex;
          }
          const companionBirthDate = parseBirthDateValue(companion.companionBirthdate);
          const companionPreviousBirthDate = parseBirthDateValue(companionUser.birthdate);
          if (companionBirthDate && !areSameCalendarDate(companionBirthDate, companionPreviousBirthDate)) {
            companionOverrides.birthdate = formatBirthdateForApi(companionBirthDate);
          }
          if (company && company !== companionUser.company) {
            companionOverrides.company = company;
            companionOverrides.workplace = company;
          }
          // La ART del acompañante es parte de su legajo, igual que la del chofer: se persiste en
          // el usuario y sólo se manda lo que cambió. El formulario únicamente la pide en
          // transportistas, así que en el resto de los flujos estos campos vienen vacíos.
          if (
            companion.companionArtCompany &&
            companion.companionArtCompany !== companionUser.artCompany
          ) {
            companionOverrides.artCompany = companion.companionArtCompany;
          }
          if (
            companion.companionArtPolicyNumber &&
            companion.companionArtPolicyNumber !== companionUser.artPolicyNumber
          ) {
            companionOverrides.artPolicyNumber = companion.companionArtPolicyNumber;
          }
          const companionArtDate = parseApiOrIsoDate(companion.companionArtExpirationDate);
          if (
            companionArtDate &&
            !areSameCalendarDate(companionArtDate, parseApiDate(companionUser.artExpirationDate))
          ) {
            companionOverrides.artExpirationDate = formatVtvExpirationDateForApi(companionArtDate);
          }

          if (Object.keys(companionOverrides).length > 0) {
            try {
              await userReq.updateUser(authStore.token, companionId, companionOverrides);
            } catch (err) {
              console.warn("Error updating companion user:", err);
            }
          }

          let companionEventId: string | undefined = companion.eventId;

          if (eventType === "out") {
            // For exit events, create a NEW event instead of reusing the entrance one.
            // If the eventId wasn't already set during search, create it now.
            if (!companionEventId) {
              const eventResponse = await newEvent.createInitialEvent(authStore.token, companionId);
              companionEventId = eventResponse?.data?.id as string | undefined;
            }

            if (!companionEventId) {
              console.warn("Companion skipped – could not obtain exit event for:", companionId);
              return;
            }
          } else if (eventType === "in") {
            // 3. For entry events: create an initial event for the companion if not already present
            if (!companionEventId) {
              const eventResponse = await newEvent.createInitialEvent(authStore.token, companionId);
              companionEventId = eventResponse?.data?.id as string | undefined;
            }

            if (!companionEventId) {
              console.warn("Companion skipped – could not create initial event for:", companionId);
              return;
            }
          }

          const finalEventId = companionEventId;
          if (!finalEventId) {
            console.warn("Companion skipped – could not obtain companionEventId");
            return;
          }

          // Upload companion images if they are Files
          if (companion.companionSelfie && companion.companionSelfie instanceof File) {
            try {
              const dataUrl = await fileToDataURL(companion.companionSelfie);
              await userReq.uploadImgUser(
                authStore.token,
                dataUrl,
                companionId,
                "facePhoto",
                fileNames.PROFILE_IMAGE_FILENAME
              );
              await userReq.uploadImgEvent(
                authStore.token,
                dataUrl,
                finalEventId,
                "facePhoto",
                fileNames.PROFILE_IMAGE_FILENAME
              );
            } catch (err) {
              console.warn("Error uploading companion selfie:", err);
            }
          }

          if (companion.companionDniFront && companion.companionDniFront instanceof File) {
            try {
              const dataUrl = await fileToDataURL(companion.companionDniFront);
              await userReq.uploadImgUser(
                authStore.token,
                dataUrl,
                companionId,
                "frontDNI",
                fileNames.FRONT_DNI_FILENAME
              );
              await userReq.uploadImgEvent(
                authStore.token,
                dataUrl,
                finalEventId,
                "frontDNI",
                fileNames.FRONT_DNI_FILENAME
              );
            } catch (err) {
              console.warn("Error uploading companion front DNI:", err);
            }
          }

          if (companion.companionDniBack && companion.companionDniBack instanceof File) {
            try {
              const dataUrl = await fileToDataURL(companion.companionDniBack);
              await userReq.uploadImgUser(
                authStore.token,
                dataUrl,
                companionId,
                "backDNI",
                fileNames.BACK_DNI_FILENAME
              );
              await userReq.uploadImgEvent(
                authStore.token,
                dataUrl,
                finalEventId,
                "backDNI",
                fileNames.BACK_DNI_FILENAME
              );
            } catch (err) {
              console.warn("Error uploading companion back DNI:", err);
            }
          }

          // La póliza de ART va al usuario y no al evento: es documentación de legajo que se
          // reutiliza en el próximo ingreso, igual que la del chofer.
          if (companion.companionArtPhoto instanceof File) {
            try {
              const dataUrl = await fileToDataURL(companion.companionArtPhoto);
              await userReq.uploadImgUser(
                authStore.token,
                dataUrl,
                companionId,
                "artPhoto1",
                fileNames.ART_PHOTO_1_FILENAME
              );
            } catch (err) {
              console.warn("Error uploading companion ART photo:", err);
            }
          }

          // 4. Clone the main event payload; only override who / eventId
          //    and add mainEvent / driver links.
          //    Todo el bloque del viaje (`pallets`, `receipt`, `seal`, origen/destino
          //    y `transportAuthorizations`) es del chofer: el acompañante no repite ni la carga ni
          //    la autorización (esta última se reemplaza más abajo por una confirmación en
          //    guardia). En los flujos no-transporte esos campos ni siquiera existen en el
          //    payload, así que descartarlos siempre es inocuo.
          const {
            invites,
            authorizations,
            prevalidations,
            hostConfirmations,
            employeeConfirmations,
            peopleComment,
            comment,
            vehicleId,
            vehicleOverrides,
            pallets,
            receipt,
            seal,
            transportOrigin,
            transportDestination,
            transportAuthorizations,
            ...sharedPayload
          } = mainEventPayload;
          void peopleComment;
          void comment;
          void vehicleId;
          void vehicleOverrides;
          void pallets;
          void receipt;
          void seal;
          void transportOrigin;
          void transportDestination;
          void transportAuthorizations;

          const companionEvent: any = {
            ...sharedPayload,
            who: companionId,
            eventId: finalEventId,
            mainEvent: mainEventPayload.eventId, // link back to parent event
            driver: mainEventPayload.who?.id || mainEventPayload.who, // main user is the driver
            /*
            company: companion.isEmployee
              ? (companion.employeeWorkplaces?.[0]?.company || "")
              : (companion.authorizationCompany || companion.employeeConfirmations?.[0]?.company || ""),
            */
          };

          // El acompañante viaja en el mismo vehículo que el chofer: se manda el id que ya resolvió
          // `verifyVehicle` para el evento principal. Fuera de transporte se sigue sin mandar.
          if (isTransport && mainEventPayload.vehicleId) {
            companionEvent.vehicleId = mainEventPayload.vehicleId;
          }

          // ---------------------------------------------------------------
          // Companion authorization resolution:
          // - Inherited driver authorizations → always employeeConfirmations
          // - Companion's own fetched authorizations → keep native type
          // - Guard-selected employees → employeeConfirmations
          // ---------------------------------------------------------------

          const companionGuestTypeValue = companion.selectedAccessMethod || "any";

          if (isTransport) {
            // En transporte el acompañante no tiene autorización propia: entra confirmado en
            // guardia por el mismo anfitrión que autorizó al chofer, con el legajo de la empresa
            // transportista. `host` puede ser null si la autorización todavía no tiene anfitrión.
            const { company, host, authorizedBy, communicationMethod, dock } =
              resolveTransportConfirmationSource(data, eventType);
            companionEvent.employeeConfirmations = [
              {
                company,
                host,
                guestsType: "transport",
                transportCompany: data.transportEmpresa?.id || "",
                // Sin anfitrión no hay manager que respalde el acceso: viajan el nombre que el
                // guardia cargó en "Responsable que autorizó el acceso" al autorizar él mismo y el
                // medio por el que la consiguió.
                ...(!host && authorizedBy ? { authorizedBy } : {}),
                ...(!host && communicationMethod ? { communicationMethod } : {}),
                // El dock asignado al chofer también aplica al acompañante: viaja igual que la
                // empresa y el anfitrión, sin depender de si hay anfitrión resoluble.
                ...(dock !== null && dock !== undefined && dock !== "" ? { dock } : {}),
              },
            ];
          } else if (eventType === "out" && companion.entranceEvent) {
            const ent = companion.entranceEvent.event || companion.entranceEvent;

            const exitInvites: string[] = [];
            const exitPrevals: string[] = [];
            const exitEmployeeConfirmations: any[] = [];

            (ent.invites || []).forEach((i: any) => {
              if (i.type === "guard") {
                const confirmation = buildExitEmployeeConfirmation(i, companionGuestTypeValue);
                if (confirmation) exitEmployeeConfirmations.push(confirmation);
              } else if (i.type === "contractor" || i.type === "eventual") {
                exitPrevals.push(i.id || i);
              } else {
                exitInvites.push(i.id || i);
              }
            });

            if (exitInvites.length > 0) companionEvent.invites = exitInvites;
            if (exitPrevals.length > 0) companionEvent.prevalidations = exitPrevals;
            if (exitEmployeeConfirmations.length > 0) {
              companionEvent.employeeConfirmations = exitEmployeeConfirmations;
            }
          } else if (eventType === "out" && !companion.isEmployee) {
            // On exit without entrance event, inherit driver's auths as employeeConfirmations
            companionEvent.employeeConfirmations = companionConfirmations.map((ec: any) => ({
              ...ec,
              guestsType: companionGuestTypeValue,
            }));
          } else if (!companion.isEmployee) {
            // Build a Set of driver authorization IDs for quick lookup
            const driverAuthIdSet = new Set(
              fullAuthorizations.map((a: any) => a.id).filter(Boolean)
            );

            // Helper: convert an authorization ID to employeeConfirmation format
            // by looking up the full auth object from fullAuthorizations
            const convertToEmployeeConfirmation = (authId: string) => {
              const auth = fullAuthorizations.find((a: any) => a.id === authId);
              if (!auth) return null;
              const hostId = auth.host?.id || auth.manager?.id || auth.host || auth.manager;
              if (!hostId) return null;
              return { host: hostId, company: auth.company || "" };
            };

            // Partition companion's selected invites/authorizations/prevalidation
            // into "inherited from driver" vs "companion's own"
            const companionInvites = (companion.invites || []).filter(Boolean);
            const companionAuths = (companion.authorizations || []).filter(Boolean);
            const companionPrevals = (companion.prevalidations || companion.prevalidation || []).filter(Boolean);

            const ownInvites: string[] = [];
            const ownAuths: string[] = [];
            const ownPrevals: string[] = [];
            const inheritedConfirmations: any[] = [];

            companionInvites.forEach((id: string) => {
              if (driverAuthIdSet.has(id)) {
                const conf = convertToEmployeeConfirmation(id);
                if (conf) inheritedConfirmations.push(conf);
              } else {
                ownInvites.push(id);
              }
            });

            companionAuths.forEach((id: string) => {
              if (driverAuthIdSet.has(id)) {
                const conf = convertToEmployeeConfirmation(id);
                if (conf) inheritedConfirmations.push(conf);
              } else {
                ownAuths.push(id);
              }
            });

            companionPrevals.forEach((id: string) => {
              if (driverAuthIdSet.has(id)) {
                const conf = convertToEmployeeConfirmation(id);
                if (conf) inheritedConfirmations.push(conf);
              } else {
                ownPrevals.push(id);
              }
            });

            // Attach companion's own authorizations in their native format
            if (ownInvites.length > 0) companionEvent.invites = ownInvites;
            if (ownAuths.length > 0) companionEvent.authorizations = ownAuths;
            if (ownPrevals.length > 0) companionEvent.prevalidations = ownPrevals;

            // Merge: inherited confirmations + guard-selected employeeConfirmations
            const guardSelected = (companion.employeeConfirmations || []).filter(
              (ec: any) => ec && ec.host
            );
            const allConfirmations = [...inheritedConfirmations, ...guardSelected];

            // If companion has no own auths and no confirmations at all,
            // fall back to the driver's pre-built companionConfirmations
            if (
              ownInvites.length === 0 &&
              ownAuths.length === 0 &&
              ownPrevals.length === 0 &&
              allConfirmations.length === 0
            ) {
              companionEvent.employeeConfirmations = companionConfirmations.map((ec: any) => ({
                ...ec,
                guestsType: companionGuestTypeValue,
              }));
            } else if (allConfirmations.length > 0) {
              companionEvent.employeeConfirmations = allConfirmations.map((ec: any) => ({
                ...ec,
                guestsType: companionGuestTypeValue,
              }));
            }
          }

          // 5. Fire the companion event
          await newEvent.addEventAll(companionEvent);
        } catch (error) {
          console.warn("Error processing companion event:", error);
        }
      })
    );
  };

  // ---------------------------------------------------------------------------
  // Main sendEvent
  // ---------------------------------------------------------------------------

  /**
   * Submit an "in" / "out" / "reject" event for the LogisticPark feature.
   * Returns `{ success: boolean }` so the caller can react accordingly.
   */
  const sendEvent = async (
    data: EntryRegistrationFormValues,
    eventType: "in" | "out" | "reject" = "in",
    fullAuthorizations: any[] = []
  ): Promise<{ success: boolean; error?: string }> => {
    // Un egreso de transportistas nunca puede salir sin autorización: esto es la última barrera
    // antes de tocar la API, independiente de la validación del formulario (que ya debería haber
    // bloqueado el submit, pero no es la única puerta de entrada a `sendEvent`).
    if (eventType === "out" && isTransportData(data) && !data.transportAutorizacionEgreso?.id) {
      return {
        success: false,
        error: "Debe seleccionar una autorización de egreso antes de registrar la salida.",
      };
    }

    setLoadingSubmit(true);
    try {
      // 1. Update user fields if the form contains editable data
      await updateUserIfNeeded(data);
      await uploadDriverDocumentsIfNeeded(data);

      // 2. Handle vehicle:
      //    - New vehicle (selectedVehicleId is "new" or empty): create via addVehicle + upload images
      //    - Existing vehicle: only upload new images (insurancePhoto) if user took a new photo
      let createdVehicleId: string | null = null;
      let vehicleAlreadyUpdated = false;
      if (!data.selectedVehicleId || data.selectedVehicleId === "new") {
        // El id del alta es con lo que el transportista identifica al camión en el evento.
        createdVehicleId = await addVehicleIfNeeded(data);
      } else {
        // Existing vehicle – upload new photos if any (mirrors NewVehicle.tsx → add())
        await uploadExistingVehicleImages(data);
        vehicleAlreadyUpdated = await updateVehicleIfNeeded(data);
      }

      // Upload bike photo if vehicle type is bike
      if (data.vehicleType === "bike") {
        await uploadBikePhotoIfNeeded(data, data.eventId || undefined);
      }

      // Resolve trailers ("Vehículos Asociados") to real vehicle ids, creating them via addVehicle
      // when they're not already registered to this driver.
      const trailerIds = await resolveTrailerVehicles(data);

      // 3. Build the event payload
      let userEvent = buildEventPayload(data, eventType);
      userEvent = verifyVehicle(userEvent, data, createdVehicleId);
      userEvent = verifyVehicleOverride(userEvent, data, vehicleAlreadyUpdated);
      userEvent = verifyAuthorizations(userEvent, data, eventType);
      userEvent = await verifyTransportAuthorizations(userEvent, data, eventType);
      if (trailerIds.length) {
        userEvent.trailers = trailerIds;
      }

      // 4. Send the main event
      const result = await newEvent.addEventAll(userEvent);

      const isOk =
        result?.response?.status === "ok" ||
        result?.status === "ok" ||
        result?.data?.status === "ok";

      if (!isOk) {
        // La API no siempre rechaza con `status: "error"` (que HttpRequest convierte en excepción):
        // cuando resuelve con otra cosa el `msg` es lo único que dice qué pasó, así que se propaga
        // en vez de dejar el toast genérico.
        const apiMessage = result?.msg || result?.response?.msg || result?.data?.msg;
        console.warn("addEvent no devolvió ok:", result);
        return {
          success: false,
          error: typeof apiMessage === "string" ? apiMessage : undefined,
        };
      }

      // 5. Process companion events — pass the fully-built main event so
      //    companions inherit the same authorizations, vehicle, etc.
      await processCompanions(data, userEvent, eventType, fullAuthorizations);

      return { success: true };
    } catch (error) {
      // La API rechaza con el `msg` del backend (ver HttpRequest.requestAll): se propaga al toast
      // en vez de perderse, que es la única forma de saber qué campo rechazó.
      console.warn("Error sending event:", error);
      const message =
        typeof error === "string" ? error : (error as any)?.message || (error as any)?.msg;
      return { success: false, error: typeof message === "string" ? message : undefined };
    } finally {
      setLoadingSubmit(false);
    }
  };

  return {
    sendEvent,
    loadingSubmit,
    saveExistingVehicle,
    saveExistingTrailerVehicle,
  };
};

export default useEvent;
